import classNames from "classnames";

import { FormattedPrice, useTranslations } from "@jti/ui";

import styles from "./ExpandedBundleItem.module.scss";

interface SubItem {
  thumbnail: {
    src: string;
    alt: string;
  };
  quantity: number;
  title: string;
  basePrice: {
    value: number;
    currency: string;
  };
  discountedPrice?: {
    value: number;
    currency: string;
  };
}

export interface ExpandedBundleItemProps {
  title: string;
  badge?: string;
  subtitle?: string;
  quantity: number;
  price: {
    value: number;
    currency: string;
  };
  subItems: SubItem[];
  areBundleItemsShown?: boolean;
  areBundlePricesEnabled?: boolean;
  thumbnail: { src: string; alt: string };
}

export const ExpandedBundleItem = ({
  title,
  badge,
  subtitle,
  quantity,
  price,
  subItems,
  areBundlePricesEnabled,
  areBundleItemsShown,
  thumbnail,
}: ExpandedBundleItemProps) => {
  const { __ } = useTranslations();

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.productDetails}>
          {!areBundleItemsShown && (
            <div className={styles.thumbnail}>
              <img src={thumbnail.src} alt={thumbnail.alt} />
            </div>
          )}
          <div className={styles.details}>
            <p className={styles.title}>{title}</p>
            {badge && <span className={styles.badge}>{__(badge)}</span>}
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>
        <div className={styles.pricingDetails}>
          <div className={styles.quantityWrapper}>
            <p className={styles.quantityText}>{__("cart.items_summary.quantity")}</p>
            <p className={styles.quantityValue}>{`×${quantity}`}</p>
          </div>
          <FormattedPrice currency={price.currency} value={price.value} className={styles.price} />
        </div>
      </div>

      {areBundleItemsShown && (
        <div className={styles.subItemsList}>
          {subItems.map((item, index) => (
            <div key={`${index}-${item.title}`} className={styles.subItem}>
              <div className={styles.subItemThumbnail}>
                <img src={item.thumbnail.src} alt={item.thumbnail.alt} />
              </div>
              <p className={styles.subItemTitle}>{`${item.quantity}× ${item.title}`}</p>

              {areBundlePricesEnabled && (
                <div className={styles.subItemPrices}>
                  {item.discountedPrice && (
                    <FormattedPrice
                      value={item.discountedPrice.value}
                      currency={item.discountedPrice.currency}
                      className={styles.subItemBasePrice}
                    />
                  )}
                  <FormattedPrice
                    value={item.basePrice.value}
                    currency={item.basePrice.currency}
                    className={classNames(
                      item.discountedPrice
                        ? [styles.subItemDiscountedPrice, styles.discounted]
                        : styles.subItemBasePrice
                    )}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
